import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import api from '../lib/api';

const TranslationContext = createContext(null);

export const TranslationProvider = ({ children }) => {
  const { language } = useAuth();
  // cache[`${lang}:${id}`] = { title, description }
  const cacheRef = useRef({});
  const pendingRef = useRef(new Set());
  const inflightRef = useRef(new Set());
  const timerRef = useRef(null);
  const [version, setVersion] = useState(0);

  const flush = useCallback(async () => {
    const lang = language;
    if (lang === 'nl') return;
    const ids = Array.from(pendingRef.current);
    pendingRef.current.clear();
    if (!ids.length) return;
    ids.forEach((id) => inflightRef.current.add(id));
    try {
      const res = await api.post('/translate/activities', { lang, ids });
      const translations = res.data?.translations || {};
      Object.entries(translations).forEach(([id, val]) => {
        cacheRef.current[`${lang}:${id}`] = val;
      });
      setVersion((v) => v + 1);
    } catch {
      /* stil falen: val terug op origineel Nederlands */
    } finally {
      ids.forEach((id) => inflightRef.current.delete(id));
    }
  }, [language]);

  const requestActivity = useCallback((id) => {
    if (!id || language === 'nl') return;
    const key = `${language}:${id}`;
    if (cacheRef.current[key] || inflightRef.current.has(id) || pendingRef.current.has(id)) return;
    pendingRef.current.add(id);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, 150);
  }, [language, flush]);

  const getActivityT = useCallback((id) => {
    if (!id || language === 'nl') return null;
    return cacheRef.current[`${language}:${id}`] || null;
  }, [language, version]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <TranslationContext.Provider value={{ requestActivity, getActivityT, language }}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslationCtx = () => {
  const ctx = useContext(TranslationContext);
  if (!ctx) throw new Error('useTranslationCtx must be used within a TranslationProvider');
  return ctx;
};

/** Hook voor kaarten: registreert de activiteit en geeft de vertaling terug
 *  (of null als er nog niets is / taal NL is). */
export const useActivityTranslation = (activity) => {
  const { requestActivity, getActivityT, language } = useTranslationCtx();
  const id = activity?.id;
  useEffect(() => {
    if (language !== 'nl' && id) requestActivity(id);
  }, [id, language, requestActivity]);
  return language !== 'nl' ? getActivityT(id) : null;
};
